/**
 * LookupItem
 *
 * Grid filter field for a lookup column: a compact autocomplete that searches the column's entity
 * and reports the chosen value and its label under the column's path.
 */

import React, { useEffect, useRef } from 'react';
import { Form } from 'semantic-ui-react';
import { I18n } from 'react-redux-i18n';
import get from 'lodash.get';
import CommonForm from '../../form';
import { AutocompleteOption } from '../../form/autocomplete';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { reset, searchEntities } from '../../../modules/autocomplete';
import { isArrayWithItems } from '../../../helpers/arrayHelper';
import { getEntityOption } from '../../../helpers/entityOptionHelper';

interface LookupItemProps {
  additionalFilters?: any;
  clearOnChange?: boolean;
  entity: string;
  /** Visible label linked to the search input. */
  fieldLabel?: string;
  filter?: any;
  getLookupItem?: (item: any) => AutocompleteOption;
  label?: string;
  onChange: (
    event: any,
    target: { value: any; label?: string | null; path: string }
  ) => void;
  path: string;
  value?: any;
}

const getLookupRequest = (filters: any[], text: string) => ({
  page: 1,
  pageSize: 10,
  resultType: 'Lookup',
  orderBy: {
    key: 'name',
    direction: 'asc'
  },
  filters,
  text
});

const LookupItem = ({
  additionalFilters,
  clearOnChange,
  entity,
  fieldLabel,
  filter,
  getLookupItem,
  label,
  onChange,
  path,
  value
}: LookupItemProps) => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state) =>
    get(state, `autocomplete.${path}.loading`)
  );
  const list: any[] | undefined = useAppSelector((state: any) =>
    get(state, `autocomplete.${path}.data.list`)
  );
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(
    () => () => {
      timer.current && clearTimeout(timer.current);
    },
    []
  );

  // Searches only typed text. Emptying the field clears the results.
  const search = (_event: any, { searchQuery }: { searchQuery: string }) => {
    timer.current && clearTimeout(timer.current);
    if (!searchQuery?.trim()) {
      dispatch(reset(path));
      return;
    }
    timer.current = setTimeout(() => {
      const baseFilters = !filter
        ? []
        : isArrayWithItems(filter)
          ? filter
          : [filter];
      dispatch(
        searchEntities(
          entity,
          path,
          getLookupRequest(baseFilters.concat(additionalFilters), searchQuery)
        )
      );
    }, 500);
  };

  const options: AutocompleteOption[] = list?.length
    ? list.map((item) =>
        getLookupItem ? getLookupItem(item) : getEntityOption(item)
      )
    : value && label
      ? [{ text: label, value }]
      : [];

  // Reports the value with its label, then the label on its own.
  const handleChange = (event: any, { value: chosen }: { value?: any }) => {
    const item = options.find((x) => x.value === chosen);
    const text = item ? item.text : null;

    onChange(event, { value: chosen, label: text, path: `${path}.value` });
    onChange(event, { value: text, path: `${path}.label` });
  };

  return (
    <Form.Group grouped>
      <CommonForm.Autocomplete
        compact
        label={fieldLabel}
        value={value}
        onChange={handleChange}
        placeholder={I18n.t('filters.componentsLabel.search')}
        search={search}
        options={options}
        loading={loading}
        clearOnChange={clearOnChange}
      />
    </Form.Group>
  );
};

export default LookupItem;
